import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import styles from './AdminUsersDashboard.module.css';
import { userApi } from '../../services/userApi';

interface User {
  User_ID: string;
  firstname: string;
  lastname: string;
  title?: string;
  mail: string;
  jobtitle?: string;
  picture?: string;
  created_at: string;
  updated_at: string;
  firm?: {
    Firm_ID: string;
    abbrev: string;
    name?: string;
    logo?: string;
  };
}

const Admin_Users_Dashboard: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const isMountedRef = useRef<boolean>(true);

  // Helper function to extract error message from API response
  const getErrorMessage = async (error: any): Promise<string> => {
    if (error?.response?.json) {
      try {
        const errorData = await error.response.json();
        return errorData.detail || errorData.message || 'An error occurred';
      } catch {
        return error.message || 'An error occurred';
      }
    } else if (error?.detail) {
      return error.detail;
    } else if (error?.message) {
      return error.message;
    } else if (typeof error === 'string') {
      return error;
    }
    return 'An unexpected error occurred';
  };

  // Simple effect to fetch users on mount
  useEffect(() => {
    let isComponentMounted = true; // Local variable to track mount state

    // Fetch users on component mount
    const fetchUsers = async () => {
      try {
        console.log('Starting to fetch users...');
        // Show loading state
        setLoading(true);
        
        const data = await userApi.getAllUsers();
        console.log('Fetched users:', data); // Add logging to debug
        console.log('Users array length:', data.length);
        
        // Check if data is valid array
        if (!Array.isArray(data)) {
          console.error('API did not return an array:', data);
          toast.error('Invalid data format received from the server', {
            position: "top-right",
            autoClose: 5000
          });
          if (isComponentMounted) {
            setLoading(false);
          }
          return;
        }
        
        // Make sure component is still mounted before updating state
        if (isComponentMounted) {
          console.log('Component is mounted, updating state with', data.length, 'users');
          
          // Update state in sequence with setTimeout to avoid React batching updates
          setUsers(data);
          
          setTimeout(() => {
            if (isComponentMounted) {
              setFilteredUsers(data);
              console.log('Filtered users updated, setting loading to false');
              setLoading(false);
            }
          }, 0);
        }
      } catch (error) {
        console.error('Error fetching users:', error);
        const errorMessage = await getErrorMessage(error);
        toast.error(errorMessage, {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
        if (isComponentMounted) {
          setLoading(false);
        }
      }
    };

    // Execute the fetch function
    console.log('Calling fetchUsers...');
    fetchUsers();

    // Cleanup function to set the local variable when the component unmounts
    return () => {
      console.log('Component unmounting, cleaning up');
      isComponentMounted = false;
      isMountedRef.current = false;
    };
  }, []);

  // Force loading to false if we have users but loading is still true
  useEffect(() => {
    if (users.length > 0 && loading) {
      console.log('Users loaded but loading state is still true, forcing loading to false');
      setLoading(false);
    }
  }, [users, loading]);

  useEffect(() => {
    // Filter users when search term changes
    console.log('Filtering users with term:', searchTerm, 'from', users.length, 'users');
    
    if (searchTerm) {
      const filtered = users.filter(user =>
        (user.firstname || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.lastname || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.mail || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.firm?.abbrev || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
      console.log('Filtered results:', filtered.length, 'users');
      setFilteredUsers(filtered);
    } else {
      console.log('No search term, setting all users:', users.length);
      setFilteredUsers(users);
    }
  }, [searchTerm, users]);

  const handleDeleteUser = async (userId: string) => {
    // Instead of window.confirm, use toast.warn with a custom UI for confirmation
    toast.warn(
      <div style={{ minWidth: '300px' }}>
        <p className="mb-3 fw-bold">Are you sure you want to delete this user?</p>
        <div className="d-flex justify-content-center gap-3">
          <button 
            className="btn btn-secondary px-4" 
            onClick={() => toast.dismiss()}
          >
            Cancel
          </button>
          <button 
            className="btn btn-danger px-4" 
            onClick={async () => {
              toast.dismiss();
              try {
                await userApi.deleteUser(userId);
                // Remove the user from state
                setUsers(prevUsers => prevUsers.filter(user => user.User_ID !== userId));
                toast.success('User deleted successfully', {
                  position: "top-right",
                  autoClose: 3000,
                  hideProgressBar: false,
                  closeOnClick: true,
                  pauseOnHover: true,
                  draggable: true
                });
              } catch (error) {
                console.error('Error deleting user:', error);
                const errorMessage = await getErrorMessage(error);
                toast.error(errorMessage, {
                  position: "top-right",
                  autoClose: 3000,
                  hideProgressBar: false,
                  closeOnClick: true,
                  pauseOnHover: true,
                  draggable: true
                });
              }
            }}
          >
            Delete
          </button>
        </div>
      </div>,
      {
        position: "top-center",
        autoClose: false,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        closeButton: false,
        className: styles.deleteConfirmationToast,
        style: { 
          maxWidth: '400px', 
          width: '100%',
          padding: '10px'
        }
      }
    );
  };

  // Direct navigation without delay as an alternative
  const navigateDirect = (path: string) => {
    console.log('Direct navigation to:', path);
    navigate(path);
  };
  
  // Avoid navigating too quickly which can cause ResizeObserver issues
  const navigateWithDelay = (path: string) => {
    console.log('Navigate with delay called to:', path);
    
    // Prevent navigation if not mounted
    if (!isMountedRef.current) {
      console.log('Not navigating - component not mounted');
      return;
    }
    
    // Short delay to allow cleanup
    try {
      setTimeout(() => {
        console.log('Executing delayed navigation to:', path);
        if (isMountedRef.current) {
          console.log('Component still mounted, navigating to:', path);
          navigate(path);
        } else {
          console.log('Component unmounted during delay, navigation cancelled');
        }
      }, 50);
    } catch (error) {
      console.error('Error in navigateWithDelay:', error);
    }
  };
  
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1)
      .toString().padStart(2, '0')}.${date.getFullYear()} ${date.getHours().toString()
      .padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  };

  return (
    <div className="container mt-4">
      
      
      {/* Toast Container with custom settings */}
      <ToastContainer 
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      
      <nav aria-label="breadcrumb">
          <ol className={`breadcrumb `}>
            <li className={`breadcrumb-item `}>
              <Link to="/admin"><i className="bi bi-house-door me-1"></i>Admin</Link>
            </li>
            <li className={`breadcrumb-item active `}>User Management</li>
          </ol>
        </nav>

    {/* Debug information */}
      {process.env.NODE_ENV === 'development' && (
        <div className="alert alert-info mb-2">
          <small>Debug: Users: {users.length}, Filtered: {filteredUsers.length}, Search: "{searchTerm}", Loading: {loading.toString()}</small>
          <button 
            onClick={() => setLoading(false)} 
            className="btn btn-sm btn-outline-primary ms-2"
          >
            Force Show Users
          </button>
          <button 
            onClick={() => console.log("Current state:", { users, filteredUsers, loading })} 
            className="btn btn-sm btn-outline-secondary ms-2"
          >
            Log State
          </button>
          {users.length > 0 && (
            <>
              <button 
                onClick={() => {
                  const firstUserId = users[0].User_ID;
                  console.log('Debug direct navigation to first user:', firstUserId);
                  navigateDirect(`/admin/users/${firstUserId}`);
                }} 
                className="btn btn-sm btn-outline-info ms-2"
              >
                Direct Navigate
              </button>
              <button 
                onClick={() => {
                  const firstUserId = users[0].User_ID;
                  console.log('Debug delayed navigation to first user:', firstUserId);
                  navigateWithDelay(`/admin/users/${firstUserId}`);
                }} 
                className="btn btn-sm btn-outline-info ms-2"
              >
                Delayed Navigate
              </button>
            </>
          )}
          {users.length > 0 && !loading && (
            <div className="mt-2">
              <small>First user: {users[0].firstname} {users[0].lastname}</small>
            </div>
          )}
        </div>
      )}

        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className={styles.searchContainer}>
            <input 
              id="search-input" 
              type="text" 
              className={`form-control ${styles.searchInput}`} 
              placeholder="Search users..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <Link className="btn btn-primary" to="/admin/users/create">
            Create new User
          </Link>
        </div>

        <div id="user-list-container" className={styles.userListContainer}>
          <div className={`${styles.mainBox} ${styles.clearfix}`}>
            <div className="table-responsive">
              <table className={`table ${styles.userTable}`}>
                <thead>
                  <tr>
                    <th className={styles.userTableHeader}>User</th>
                    <th className={styles.userTableHeader}>Firm</th>
                    <th className={styles.userTableHeader}>Created</th>
                    <th className={styles.userTableHeader}>Updated</th>
                    <th className={styles.userTableHeader}>&nbsp;</th>
                  </tr>
                </thead>
                <tbody id="user-list">
                  {loading ? (
                    <tr>
                      <td colSpan={5} className="text-center p-5">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <div className="mt-2">Loading users...</div>
                      </td>
                    </tr>
                  ) : filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center">
                        {users.length > 0 ? 
                          'No users match your search criteria' : 
                          'No users found. Please check API connection.'}
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map(user => (
                      <tr key={user.User_ID}>
                        <td className={styles.userTableCell}>
                          <div className={styles.userInfo}>
                            {user.picture ? (
                              <img src={`data:image/png;base64,${user.picture}`} alt={`${user.firstname} ${user.lastname}`} className={styles.userAvatar} />
                            ) : (
                              <div className={styles.userAvatar} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#e9ecef' }}>
                                <i className="bi bi-person-fill text-secondary"></i>
                              </div>
                            )}
                            <div className={styles.userDetails}>
                              <a 
                                href="#" 
                                onClick={(e) => {
                                  e.preventDefault();
                                  console.log('User name clicked for user ID:', user.User_ID);
                                  navigateWithDelay(`/admin/users/${user.User_ID}`);
                                }} 
                                className={styles.userLink}
                              >
                                {user.title ? `${user.title} ` : ''}{user.firstname} {user.lastname}
                              </a>
                              <a href={`mailto:${user.mail}`} className={styles.userEmail}>{user.mail}</a>
                              {user.jobtitle && <div className={styles.userJobtitle}>{user.jobtitle}</div>}
                            </div>
                          </div>
                        </td>

                        <td className={styles.userTableCell}>
                          {user.firm ? (
                            <div className={styles.userFirm}>
                              <img src={user.firm.logo ? `data:image/png;base64,${user.firm.logo}` : '/static/images/No_image_available.png'} 
                                 alt={user.firm.abbrev} 
                                 className={styles.firmLogo} />
                              <span>{user.firm.name || user.firm.abbrev}</span>
                            </div>
                          ) : (
                            <span className="text-muted">No firm</span>
                          )}
                        </td>
                        <td className={styles.userTableCell}>
                          {formatDate(user.created_at)}
                        </td>
                        <td className={styles.userTableCell}>
                          {formatDate(user.updated_at)}
                        </td>
                        <td className={`text-end ${styles.userTableCell}`}>
                          <div className={`btn-group ${styles.actionButtonsContainer}`}>
                            <button 
                              className={`btn btn-sm btn-outline-primary ${styles.actionButton}`} 
                              onClick={(e) => {
                                e.preventDefault();
                                console.log('Edit button clicked for user ID:', user.User_ID);
                                // Try direct navigation instead of delayed
                                navigateDirect(`/admin/users/${user.User_ID}`);
                              }}
                            >
                              <i className="bi bi-pencil-fill"></i>
                            </button>
                            <button 
                              className={`btn btn-sm btn-outline-danger ${styles.actionButton}`} 
                              onClick={() => handleDeleteUser(user.User_ID)}
                            >
                              <i className="bi bi-trash-fill"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
   
    </div>
  );
};

export default Admin_Users_Dashboard;
